import React, { useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import QRCodeSVG from 'react-native-qrcode-svg'
import { Button, Icon, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { eventPriceLabel, formatCountdown, formatEventPrice, getCommonStyles } from '@utils'
import {
  type EventDetailResponse,
  useCountdown,
  useJoinEvent,
  useLeaveEvent,
  useStartEventPayment,
  useUpdateEventProfile,
} from '@hooks'
import { EventConsentNotice } from './EventConsentNotice'

interface ViewerPanelProps {
  detail: EventDetailResponse
  onManage: () => void
  onViewTicket: () => void
}

/** Right-hand panel for a logged-in visitor: join, registered status, or organizer shortcut. */
export function ViewerPanel({ detail, onManage, onViewTicket }: ViewerPanelProps) {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const joinEvent = useJoinEvent()
  const leaveEvent = useLeaveEvent()
  const { startPayment, openPayment, isStarting } = useStartEventPayment()
  const { event, viewer } = detail
  const isPaid = event.priceCents !== null
  const price = eventPriceLabel(event, '', currentLanguage)
  const heldMs = useCountdown(viewer.reservation?.expiresAt)
  const [confirmLeave, setConfirmLeave] = useState(false)

  if (viewer.role === 'organizer') {
    return (
      <View style={styles.panel}>
        <View style={styles.badge}>
          <Icon name="star" size={16} color={theme.colors.gold} />
          <Text type="bodySmallBold" color={theme.colors.gold} style={styles.badgeText}>
            {t('events.public.youOrganize')}
          </Text>
        </View>
        <Button
          title={t('events.public.manage')}
          onPress={onManage}
          iconName="settings"
          containerStyle={styles.fullWidth}
        />
      </View>
    )
  }

  if (viewer.role === 'none') {
    const closed = !event.registrationOpen || event.isFull
    return (
      <View style={styles.panel}>
        <Text type="titleBold">{t('events.join.title')}</Text>
        <Text type="body" color={theme.colors.greyBlue} style={styles.lead}>
          {closed
            ? event.isFull
              ? // Seats can be "full" only because people are paying right now.
                t(event.attendeeCount === 0 ? 'events.join.fullHeld' : 'events.join.full')
              : t('events.join.closed')
            : t('events.join.subtitle')}
        </Text>
        {!closed && viewer.reservation && heldMs > 0 ? (
          <View style={styles.reserved} testID="seat-reserved">
            <Text type="bodyBold" color={theme.colors.orange}>
              {t('events.payment.seatHeld', { time: formatCountdown(heldMs) })}
            </Text>
            <Text type="bodySmall" color={theme.colors.secondary} style={styles.lead}>
              {t('events.payment.reservedHint')}
            </Text>
            <Button
              title={t('events.payment.resume', { price })}
              testID="resume-payment"
              onPress={() => viewer.reservation && openPayment(viewer.reservation)}
              containerStyle={styles.fullWidth}
            />
          </View>
        ) : null}
        {!closed && !(viewer.reservation && heldMs > 0) ? (
          <>
            <Button
              title={
                isPaid ? t('events.public.reserveSeat', { price }) : t('events.public.joinOneClick')
              }
              testID="join-button"
              disabled={joinEvent.isPending || isStarting}
              onPress={() =>
                joinEvent.mutate(event.code, {
                  onSuccess: ({ attendance }) => {
                    if (attendance.paymentRequired) void startPayment({ id: event.id })
                  },
                })
              }
              containerStyle={styles.fullWidth}
            />
            {isPaid && event.serviceFeeCents > 0 ? (
              <Text
                type="bodySmall"
                color={theme.colors.secondary}
                style={styles.lead}
                testID="service-fee-note"
              >
                {t('events.public.serviceFee', {
                  fee: formatEventPrice(event.serviceFeeCents, currentLanguage, event.currency),
                })}
              </Text>
            ) : null}
            <EventConsentNotice event={event} />
          </>
        ) : null}
      </View>
    )
  }

  return (
    <View style={styles.stack}>
      {/* First: on a phone the page is taller than the screen, and this is the one thing asked of them. */}
      {!viewer.profile.jobTitle ? <CompleteProfileCard /> : null}
      <View style={styles.panel}>
        <View style={styles.badge}>
          <Icon name="check" size={16} color={theme.colors.green} />
          <Text type="bodySmallBold" color={theme.colors.green} style={styles.badgeText}>
            {t('events.public.youAreRegistered')}
          </Text>
        </View>
        <Text type="body" color={theme.colors.secondary} style={styles.lead}>
          {event.phase === 'ended'
            ? t('events.public.endedIntro')
            : t('events.public.registeredHint')}
        </Text>
        {event.phase !== 'ended' && viewer.ticketCode ? (
          <View style={styles.ticket} testID="event-ticket">
            <QRCodeSVG
              value={viewer.ticketCode}
              size={160}
              color={theme.colors.primary}
              backgroundColor={theme.colors.white}
            />
            <Text type="bodyBold" center>
              {t('events.public.ticketTitle')}
            </Text>
            <Text type="bodySmall" color={theme.colors.greyBlue} center>
              {t('events.public.ticketHint')}
            </Text>
            <Button
              title={t('events.public.viewFullScreen')}
              testID="view-ticket-full-screen"
              isSmall
              unfilled
              onPress={onViewTicket}
              containerStyle={styles.fullWidth}
            />
          </View>
        ) : null}
        {event.phase !== 'ended' && confirmLeave ? (
          <View style={styles.reserved} testID="leave-confirm">
            <Text type="bodyBold">{t('events.public.leaveConfirm')}</Text>
            {isPaid ? (
              <Text type="bodySmall" color={theme.colors.secondary} style={styles.lead}>
                {t('events.public.leavePaidConfirm')}
              </Text>
            ) : null}
            <View style={styles.row}>
              <Button
                title={t('events.public.leaveConfirmYes')}
                testID="leave-confirm-yes"
                isSmall
                isRed
                disabled={leaveEvent.isPending}
                onPress={() =>
                  leaveEvent.mutate(event.id, { onSettled: () => setConfirmLeave(false) })
                }
              />
              <Button
                title={t('events.public.leaveConfirmNo')}
                isSmall
                unfilled
                onPress={() => setConfirmLeave(false)}
              />
            </View>
          </View>
        ) : event.phase !== 'ended' ? (
          <Button
            title={t('events.public.leave')}
            testID="leave-button"
            unfilled
            isSmall
            disabled={leaveEvent.isPending}
            onPress={() => setConfirmLeave(true)}
          />
        ) : null}
      </View>
    </View>
  )
}

/** Shown after a LinkedIn sign-up: LinkedIn never shares the job title. */
function CompleteProfileCard() {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const update = useUpdateEventProfile()
  const [jobTitle, setJobTitle] = useState('')
  const [company, setCompany] = useState('')
  const [linkedinUrl, setLinkedinUrl] = useState('')
  const [hidden, setHidden] = useState(false)
  if (hidden) return null

  return (
    <View style={[styles.panel, styles.highlight]}>
      <Text type="subtitleBold">{t('events.completeProfile.title')}</Text>
      <Text type="bodySmall" color={theme.colors.secondary} style={styles.lead}>
        {t('events.completeProfile.subtitle')}
      </Text>
      <TextInput
        placeholder={t('events.join.jobTitle')}
        value={jobTitle}
        onChangeText={setJobTitle}
        iconRight="work"
      />
      <TextInput placeholder={t('events.join.company')} value={company} onChangeText={setCompany} />
      <TextInput
        placeholder={t('events.completeProfile.linkedinUrl')}
        value={linkedinUrl}
        onChangeText={setLinkedinUrl}
        autoCapitalize="none"
      />
      <View style={styles.row}>
        <Button
          title={t('events.completeProfile.save')}
          isSmall
          disabled={update.isPending || jobTitle.trim().length < 2}
          onPress={() =>
            update.mutate({
              jobTitle: jobTitle.trim(),
              company: company.trim() || undefined,
              linkedinUrl: linkedinUrl.trim() || undefined,
            })
          }
        />
        <Button
          title={t('events.completeProfile.later')}
          isSmall
          unfilled
          onPress={() => setHidden(true)}
        />
      </View>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    stack: { gap: theme.spacing.lg },
    panel: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      padding: theme.spacing.xxl,
      gap: theme.spacing.md,
      ...getCommonStyles(theme).shadow,
    },
    ticket: {
      alignItems: 'center',
      gap: theme.spacing.xs,
      padding: theme.spacing.lg,
      borderRadius: theme.border.medium,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    reserved: {
      gap: theme.spacing.sm,
      padding: theme.spacing.md,
      borderRadius: theme.border.medium,
      backgroundColor: theme.colors.sidebar,
    },
    highlight: { borderColor: theme.colors.primary, backgroundColor: theme.colors.sidebar },
    badge: { flexDirection: 'row', alignItems: 'center' },
    badgeText: { marginLeft: theme.spacing.xs },
    lead: { lineHeight: 21 },
    fullWidth: { width: '100%' },
    row: { flexDirection: 'row', gap: theme.spacing.sm, flexWrap: 'wrap' },
  })
